import React, { useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Shield, Mail, Key, UserCircle, Save, Lock, User, Camera, Calendar, Eye, EyeOff } from 'lucide-react';
import Modal from '../components/Modal';

const Settings: React.FC = () => {
  const { user, fetchUser } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [birthDate, setBirthDate] = useState(user?.birth_date ? user.birth_date.split('T')[0] : '');
  const [profilePicUrl, setProfilePicUrl] = useState(user?.profile_pic_url || '');
  const [isPublic, setIsPublic] = useState(user?.is_public ?? true);
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState<any>({ isOpen: false, title: '', message: '', type: 'info' });

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/users/${user.id}`, {
        full_name: fullName,
        birth_date: birthDate ? new Date(birthDate).toISOString() : null,
        profile_pic_url: profilePicUrl,
        is_public: isPublic
      });
      setModalConfig({ isOpen: true, title: 'Sucesso', message: 'Perfil atualizado.', type: 'success' });
      if (fetchUser) await fetchUser();
    } catch (err) {
      setModalConfig({ isOpen: true, title: 'Erro', message: 'Falha ao atualizar.', type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
        setModalConfig({ isOpen: true, title: 'Erro', message: 'Mínimo 8 caracteres.', type: 'danger' });
        return;
    }
    if (newPassword !== confirmPassword) {
        setModalConfig({ isOpen: true, title: 'Erro', message: 'As passwords não coincidem.', type: 'danger' });
        return;
    }
    setLoading(true);
    try {
      await api.post('/change-password', { current_password: currentPassword, new_password: newPassword });
      setModalConfig({ isOpen: true, title: 'Sucesso', message: 'Password alterada.', type: 'success' });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err: any) {
      setModalConfig({ isOpen: true, title: 'Erro', message: err.response?.data?.detail || 'Erro ao alterar.', type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-medium text-slate-700 dark:text-white placeholder:text-slate-400 placeholder:font-normal";
  const labelClass = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2";

  return (
    <div className="max-w-5xl mx-auto space-y-8 py-10 animate-in fade-in duration-300 pb-20">
      <Modal {...modalConfig} onClose={() => setModalConfig({ ...modalConfig, isOpen: false })} cancelText="Fechar" />

      <div>
        <h2 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Definições</h2>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Gere o teu perfil e a segurança da conta.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-6">
          <div className="flex flex-col items-center p-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl shadow-sm">
            {profilePicUrl ? (
                <img src={profilePicUrl} alt="Perfil" className="w-28 h-28 rounded-2xl object-cover" />
            ) : (
                <div className="w-28 h-28 bg-blue-600 rounded-2xl flex items-center justify-center text-white text-3xl font-semibold uppercase">{user?.email[0]}</div>
            )}
            <div className="text-center mt-5">
              <p className="font-semibold text-slate-900 dark:text-white truncate max-w-[200px]">{user?.full_name || user?.email}</p>
              <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 px-3 py-1 rounded-full font-medium mt-2 inline-block">{user?.global_role}</span>
            </div>
          </div>

          <button
            onClick={() => setIsPublic(!isPublic)}
            className={`w-full p-5 rounded-3xl border transition-all flex items-center justify-between ${isPublic ? 'bg-emerald-50/60 border-emerald-100 text-emerald-700 dark:bg-emerald-900/10 dark:border-emerald-900/20 dark:text-emerald-400' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500'}`}
          >
            <div className="flex items-center gap-3">
                {isPublic ? <Eye size={18}/> : <EyeOff size={18}/>}
                <span className="text-sm font-medium">{isPublic ? 'Perfil público' : 'Perfil privado'}</span>
            </div>
            <div className={`w-10 h-5 rounded-full relative transition-colors shrink-0 ${isPublic ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${isPublic ? 'right-1' : 'left-1'}`}></div>
            </div>
          </button>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-700 pb-4 mb-6 flex items-center gap-2">
                <UserCircle size={16} className="text-blue-600" /> <span>Informação pessoal</span>
            </h3>
            <form onSubmit={handleUpdateProfile} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className={labelClass}>Nome completo</label>
                        <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputClass} placeholder="O teu nome" />
                    </div>
                    <div>
                        <label className={labelClass}>Data de nascimento</label>
                        <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} className={inputClass} />
                    </div>
                </div>
                <div>
                    <label className={labelClass}>Foto de perfil (URL)</label>
                    <input type="text" value={profilePicUrl} onChange={(e) => setProfilePicUrl(e.target.value)} className={inputClass} placeholder="https://…" />
                </div>
                <div className="flex justify-end">
                    <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-slate-900 dark:bg-blue-600 text-white font-semibold text-sm py-3 px-6 rounded-xl hover:bg-black dark:hover:bg-blue-700 transition-all disabled:opacity-60">
                        <Save size={16} /> <span>{loading ? 'A guardar…' : 'Guardar alterações'}</span>
                    </button>
                </div>
            </form>
          </div>

          <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-700 pb-4 mb-6 flex items-center gap-2">
                <Key size={16} className="text-blue-600" /> <span>Segurança</span>
            </h3>
            <form onSubmit={handleChangePassword} className="space-y-5">
                <div>
                    <label className={labelClass}>Password atual</label>
                    <input type="password" placeholder="••••••••" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={inputClass} required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className={labelClass}>Nova password</label>
                        <input type="password" placeholder="Mínimo 8 caracteres" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputClass} required />
                    </div>
                    <div>
                        <label className={labelClass}>Confirmar password</label>
                        <input type="password" placeholder="Repete a nova password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClass} required />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold text-sm py-3 px-6 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all disabled:opacity-60">
                        <Lock size={16} /> <span>Atualizar password</span>
                    </button>
                </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
